import React from 'react';
import { Box, Button, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import type { PlanStep } from '../agent/taskPlan';
import { PLAN_CHECK_LABELS, PLAN_STATUS_ICONS } from '../agent/taskPlan';

// 手順カード（2026-10-30）。複数の手順がある依頼で AI が最初に出す。
// ［この手順で進める］で承認、［手順を直す］で出し直し。進むたびに各行の印が変わる。

interface Props {
  title: string;
  steps: PlanStep[];
  state: 'pending' | 'approved' | 'rejected' | 'superseded' | 'done';
  onResolve?: (approved: boolean) => void;
}

const STATE_CHIP: Record<string, { label: string; color: 'success' | 'default' | 'primary' }> = {
  approved: { label: '進行中', color: 'primary' },
  done: { label: '全部の手順が完了', color: 'success' },
  rejected: { label: '手順を直します', color: 'default' },
  superseded: { label: '新しい手順に差し替え', color: 'default' },
};

export default function AgentPlanCard({ title, steps, state, onResolve }: Props) {
  const dim = state === 'superseded' || state === 'rejected';
  const doneCount = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  return (
    <Box sx={{ mx: 1.5, my: 0.8, opacity: dim ? 0.6 : 1 }}>
      <Box sx={{ border: '2px solid #0d9488', borderRadius: 2, overflow: 'hidden', background: '#f0fdfa' }}>
        <Box sx={{ px: 1.5, py: 0.8, background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', color: '#fff' }}>
          <Typography variant="subtitle2" fontWeight={800}>
            📝 作業の手順（{doneCount}/{steps.length}）
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.95 }}>{title}</Typography>
        </Box>
        <Box sx={{ p: 1.2 }}>
          {steps.map((s, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.5, px: 0.8, my: 0.3,
                borderRadius: 1, background: '#fff', border: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Typography variant="caption" sx={{ minWidth: 34, fontWeight: 800 }}>
                {PLAN_STATUS_ICONS[s.status]} {i + 1}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ fontWeight: 700, textDecoration: s.status === 'skipped' ? 'line-through' : 'none' }}
                >
                  {s.label}
                </Typography>
                <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                  {s.actor === 'staff' ? '👤 スタッフ' : '🤖 AI'}・{PLAN_CHECK_LABELS[s.check]}
                  {s.status === 'waiting_staff' ? '・スタッフの操作待ち' : ''}
                </Typography>
                {s.note && (
                  <Typography variant="caption" component="div" sx={{ color: '#0f766e' }}>{s.note}</Typography>
                )}
              </Box>
            </Box>
          ))}

          {state === 'pending' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.2, flexWrap: 'wrap' }}>
              <Button
                size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                onClick={() => onResolve?.(true)} sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                この手順で進める
              </Button>
              <Button
                size="small" variant="outlined" color="inherit" startIcon={<EditIcon />}
                onClick={() => onResolve?.(false)} sx={{ textTransform: 'none' }}
              >
                手順を直す
              </Button>
            </Box>
          )}
          {STATE_CHIP[state] && (
            <Chip label={STATE_CHIP[state].label} color={STATE_CHIP[state].color} size="small" sx={{ mt: 1 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
